import React, { ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import DaumPostcodeEmbed from "react-daum-postcode";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "user/components/ui/sheet";
import Checkbox from "user/components/form/Checkbox";
import Header from "user/components/common/Header";
import Spinner from "common/components/spinner";
import { cancelMessage, KeywordSearchOverlay, NameSearchOverlay, SystemPopup } from "user/components/common/Overlay";
import Progressbar from "./-components/Progressbar";
import { SimpleOrderItem } from "./-components/OrderItem";
import { OutletContextProps } from "./-components/formik-utils";
import useOverlay, { OverlayWrapper } from "common/hooks/user/useOverlay";
import InputField, { MobileInputField, TextareaField } from "user/components/common/InputField";
import { useGetProductInfo } from "common/hooks/user/Products/useGetProductInfo";
import { useGetCommonEvent } from "common/hooks/common/useGetCommonEvent";
import { useGetCommonMemo } from "common/hooks/common/useGetCommonMemo";
import { useGetCommonTargetCustomer } from "common/hooks/common/useGetCommonTargetCustomer";
import { useAuthStore } from "user/store/useAuthStore";
import { enum_api_uri } from "common/hooks/utils/enum";
import { OrderFileProps } from "common/hooks/user/order/useGetOrderInfo";
import RefreshRedirect from "./-components/RefreshRedirect";
import { formatDeliveryDate, formatDisplayDateStringToDate, formatDisplayDeliveryDate } from "common/hooks/utils/utils";
import { ReactComponent as SearchIcon } from "../../assets/icons/searchIcon.svg";
import { ReactComponent as Arrow } from "../../assets/icons/rightArrow.svg";
import { ReactComponent as SelectIcon } from "../../../common/assets/icons/selectIcon.svg";
import { ReactComponent as CalendarIcon } from "../../assets/icons/calendar_black.svg";
import { ReactComponent as FileUploadIcon } from "../../assets/icons/file_upload.svg";
import { ReactComponent as CloseWhite } from "../../assets/icons/close_black.svg";
import "swiper/css";
import "react-day-picker/style.css";

// 첨부할 수 있는 이미지 mimetype
const allowedMimeTypes = ["image/png", "image/jpg", "image/jpeg"];
// 첨부 파일 최대 개수 (백엔드 multer maxCount 와 동일)
const MAX_FILE_COUNT = 4;
// 첨부 파일 개별 용량 제한 MB (백엔드 .env FILESIZE 와 동일)
const MAX_FILE_SIZE_MB = 20;

// 우편번호 선택 UI 스타일
const themeObj = {
    bgColor: "#FFFFFF",
    pageBgColor: "#FFFFFF",
    postcodeTextColor: "#747883",
    emphTextColor: "#222222",
};
const postCodeStyle = {
    width: "100%",
    height: "480px",
    maxWidth: "412px",
};

// 배송시간 관련
const deliveryHour = [
    "오전중",
    "오후중",
    "당일배송",
    "오전 8시",
    "오전 9시",
    "오전 10시",
    "오전 11시",
    "오후 12시",
    "오후 1시",
    "오후 2시",
    "오후 3시",
    "오후 4시",
    "오후 5시",
    "오후 6시",
    "오후 7시",
    "오후 8시",
];
const deliveryMinute = ["00분", "10분", "20분", "30분", "40분", "50분"];
const deliveryType = ["까지", "예식", "행사", "도착"];

// Progressbar 스텝
const step = 3;
const sections = ["product", "recipient", "message", "delivery"];

export default function To({ update = false }: { update?: boolean }) {
    const params = useParams();
    const navigate = useNavigate();
    const user = useAuthStore(state => state);
    const isIndividualCustomer = user?.m_category === 2; // 개인 회원 일 때
    const [viewDetail, setViewDetail] = useState(false);
    const [activeSection, setActiveSection] = useState(sections[0]);
    const productRef = useRef<HTMLDivElement | null>(null);
    const recipientRef = useRef<HTMLDivElement | null>(null);
    const messageRef = useRef<HTMLDivElement | null>(null);
    const deliveryRef = useRef<HTMLDivElement | null>(null);
    const sheetTriggerRef = useRef<HTMLButtonElement | null>(null);
    const datePickerSheetTriggerRef = useRef<HTMLButtonElement | null>(null);
    const addressSheetTriggerRef = useRef<HTMLButtonElement | null>(null);
    const eventIdxSheetTiggerRef = useRef<HTMLButtonElement | null>(null);
    const event2IdxSheetTiggerRef = useRef<HTMLButtonElement | null>(null);
    const deliveryHourSheetTiggerRef = useRef<HTMLButtonElement | null>(null);
    const deliveryMinuteSheetTiggerRef = useRef<HTMLButtonElement | null>(null);
    const deliveryTypeSheetTiggerRef = useRef<HTMLButtonElement | null>(null);
    const eventInputRef = useRef<HTMLInputElement | null>(null);
    const { overlay, closeOverlay, openOverlay } = useOverlay();
    const context = useOutletContext<OutletContextProps>();
    const formik = context.formik;
    const isSubmitting = context.isSubmitting ?? false;
    const { values, handleChange, setFieldValue, setValues, touched, errors } = formik.to;
    const [pickerDay, setPickerDay] = useState<Date>(
        values.delivery_date ? new Date(formatDisplayDateStringToDate(values.delivery_date)) : new Date(),
    );
    const [fileState, setFileState] = useState<{ file: any[]; del_file_idx: number[] }>({
        file: values.order_file ?? [],
        del_file_idx: [],
    });

    // console.log(formik);
    const errorKeys = Object.keys(errors);
    const customer_idx = formik.from.values.customer_idx;
    const product_idx = formik.product.values.product_idx;
    const product_category_idx = formik.product.values.category_idx;
    const { data: productInfo } = useGetProductInfo(Number(product_idx));
    const { data: customerData } = useGetCommonTargetCustomer(customer_idx!, product_category_idx!);
    // console.log("To customerData", customerData);

    const { data: eventData } = useGetCommonEvent(undefined, customer_idx ? Number(customer_idx) : undefined);
    const event = eventData?.data.find(data => data.depth1_idx === values.depth1_idx)?.depth1;
    const { data: event2Data } = useGetCommonEvent(Number(values.depth1_idx ?? 0), customer_idx ? Number(customer_idx) : undefined);

    const { data: deliveryMemo } = useGetCommonMemo();
    const targetDeliveryMemo = deliveryMemo?.data.find(memo => memo.delivery_memo_idx === values.delivery_memo_idx)?.delivery_memo;

    // const [selectedWreathSenderType, setSelectedWreathSenderType] = useState<"custom" | "select">("select");

    // 경조내용 에러 메시지
    const isErrorEventContent =
        (touched.depth1_idx && errors.depth1_idx) || (values.depth1_idx !== 1 && touched.event_content && errors.event_content);
    // 배송시간 에러 메시지
    const isSpecialDeliveryHour =
        values.delivery_hour === "오전중" || values.delivery_hour === "오후중" || values.delivery_hour === "당일배송";
    const isErrorDeliveryTime =
        (touched.delivery_hour && errors.delivery_hour) ||
        (!isSpecialDeliveryHour && touched.delivery_minute && errors.delivery_minute) ||
        (!isSpecialDeliveryHour && touched.delivery_type && errors.delivery_type);

    // 첨부파일 tooltip
    const filesName = fileState?.file.map(file => `\n${file.name}`);
    const inputTooltipDescription =
        fileState.file.length > 0 ? `선택된 파일 ${fileState.file.length}개, ${filesName.toString()}` : "선택된 파일 없음";

    // 상단 섹션 메뉴 클릭 시 위치 이동
    const onClickSection = (section: React.RefObject<HTMLDivElement | null>) => {
        section.current?.scrollIntoView({ behavior: "smooth" });
    };
    const handleScroll = () => {
        const container = document.getElementById("container");
        const scrollPosition = container?.scrollTop || 0; // 기본값을 0으로 설정

        sections.forEach(section => {
            const sectionElement = document.getElementById(section);
            if (sectionElement) {
                const sectionTop = sectionElement.offsetTop - 130;
                const sectionHeight = sectionElement.offsetHeight;

                if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
                    setActiveSection(section);
                }
            }
        });
    };

    useLayoutEffect(() => {
        const container = document.getElementById("container");
        if (container) {
            container.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (container) {
                container.removeEventListener("scroll", handleScroll);
            }
        };
    }, []);

    // 카테고리 노조 & 화환 설정 체크 했을 때
    useEffect(() => {
        const customerCategory = customerData?.data.product_category;

        if (customerCategory) {
            // 카테고리 화환 옵션 노출 설정시
            const isVisibleWreathOptionFields = customerCategory.wreath_option;
            // 경조내용 개별 폼 노출 설정시
            const isVisibleEventContentField = customerData.data.event_content_option; // 경조 내용 폼

            if (!isVisibleWreathOptionFields) {
                setFieldValue("is_visible_event_content_field", false);
                setFieldValue("event_content_idx", null);
                setFieldValue("depth1_idx", null);
                setFieldValue("event_content", "");
                setFieldValue("related_person", "");
                setFieldValue("department", "");
                setFieldValue("work_location", "");
                setFieldValue("company_tel", "");
                setFieldValue("wreath_sender", ""); // 명의
                setFieldValue("wreath_word", ""); // 경조사어
            } else {
                if (isVisibleEventContentField) {
                    setFieldValue("is_visible_event_content_field", true);
                } else {
                    setFieldValue("is_visible_event_content_field", false);
                }

                if (customerData.data.related_person_option) {
                    setFieldValue("related_person", values.related_person ?? "");
                }

                if (customerData.data.department_option) {
                    setFieldValue("department", values.department ?? "");
                }

                if (customerData.data.work_location_option) {
                    setFieldValue("work_location", values.work_location ?? "");
                }

                if (customerData.data.company_tel_option) {
                    setFieldValue("company_tel", values.company_tel ?? "");
                }

                if (customerData.data.wreath_sender_option) {
                    setFieldValue("wreath_sender", values.wreath_sender ?? ""); // 명의
                }

                if (customerData.data.wreath_word_option) {
                    setFieldValue("wreath_word", values.wreath_word ?? ""); // 경조사어
                }
            }
        }
    }, [customerData?.data.product_category]);

    // 파일이 바뀌면 order_file 업데이트
    useEffect(() => {
        setValues({
            ...values,
            order_file: fileState.file,
            del_file_idx: fileState.del_file_idx,
        });
    }, [fileState]);

    useEffect(() => {
        const status = formik.to.status;
        if (status?.state === "error") {
            openOverlay(
                <SystemPopup
                    message={status?.msg}
                    onClickClose={() => {
                        closeOverlay();
                        formik.to.setStatus({ state: null, msg: null });
                    }}
                />,
            );
            return;
        }
    }, [formik.to.status]);

    useEffect(() => {
        if (values.depth1_idx === 2) {
            eventInputRef.current?.focus();
        }
    }, [values.depth1_idx]);

    // 우편번호 선택 시
    const completeHandler = (data: any) => {
        const { zonecode, roadAddress, jibunAddress, buildingName, userSelectedType } = data;
        let address = "";

        // 사용자가 도로명 주소 선택시
        if (userSelectedType === "R") {
            address = roadAddress + " " + (buildingName ? `(${buildingName})` : "");
            // 사용자가 지번 주소 선택시
        } else if (userSelectedType === "J") {
            address = jibunAddress;
        }

        setValues({
            ...values,
            delivery_post: zonecode,
            delivery_address: address,
        });

        // 주소 찾기 닫기
        addressSheetTriggerRef?.current?.click();
    };

    const closeHandler = (state: string) => {};

    // 파일 선택 시
    const handleChangeFile = (files: FileList) => {
        const selectedFilesArray = Array.from(files);
        // 파일 첨부 개수 제한
        if (selectedFilesArray.length + fileState.file.length > MAX_FILE_COUNT) {
            openOverlay(
                <SystemPopup message={`파일 첨부는 최대 ${MAX_FILE_COUNT}개 까지 가능합니다.`} onClickClose={closeOverlay} />,
            );
            return;
        }
        // 파일 개별 용량 제한
        const oversized = selectedFilesArray.find(file => file.size > MAX_FILE_SIZE_MB * 1024 * 1024);
        if (oversized) {
            openOverlay(
                <SystemPopup
                    message={`파일 용량은 최대 ${MAX_FILE_SIZE_MB}MB 까지 가능합니다.`}
                    onClickClose={closeOverlay}
                />,
            );
            return;
        }
        setFileState((prev: any) => {
            return {
                ...prev,
                file: prev.file.concat(selectedFilesArray),
            };
        });
    };

    // 주문 신청 클릭 시 - 유효성 검사 후 에러 있으면 팝업 표시 + 해당 필드로 스크롤
    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationErrors = await formik.to.validateForm();

        if (Object.keys(validationErrors).length > 0) {
            const touchedFields = Object.keys(validationErrors).reduce(
                (acc, key) => ({ ...acc, [key]: true }),
                {} as Record<string, boolean>,
            );
            formik.to.setTouched(touchedFields, false);

            const firstErrorKey = Object.keys(validationErrors)[0];
            // delivery_time은 가상 키 (실제 입력 element는 delivery_hour부터)
            const targetId = firstErrorKey === "delivery_time" ? "delivery_hour" : firstErrorKey;
            document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "center" });

            const firstErrorMsg = Object.values(validationErrors)[0] as string;
            openOverlay(<SystemPopup message={firstErrorMsg} onClickClose={closeOverlay} />);
            return;
        }

        formik.to.submitForm();
    };

    // 파일 삭제 시
    const handleDeleteFile = async (file: File | OrderFileProps) => {
        // 업로드 파일 삭제
        if (file instanceof File) {
            setFileState(prev => {
                const newState = {
                    ...prev,
                    file: prev.file.filter(prevFile => prevFile !== file),
                };
                return newState;
            });
            return;
        }

        // 기존 파일 삭제
        setFileState(prev => {
            const targetFileIdx = file.file_idx;
            const newState = {
                ...prev,
                file: prev.file?.filter(prevFile => prevFile.file_idx !== targetFileIdx),
                del_file_idx: [...prev.del_file_idx, targetFileIdx],
            };
            return newState;
        });
    };

    return (
        <RefreshRedirect customerIdx={customer_idx} isUpdate={update}>
            {isSubmitting && <Spinner />}
            <OverlayWrapper overlay={overlay} closeOverlay={closeOverlay}>
                <div className={`flex flex-col ${viewDetail && "max-h-[100dvh] overflow-hidden"}`}>
                    <div className="sticky top-0 bg-white z-[1]">
                        <Header
                            title="3. 받으시는 분"
                            goBack
                            onClickClose={() => openOverlay(<SystemPopup isConfirm message={cancelMessage(update)} onClickClose={closeOverlay} />)}
                        />
                        <Progressbar step={step} className="wrapper" />
                        <ul className="wrapper flex gap-[4px] py-[8px]">
                            <li
                                className={`${activeSection === "product" ? "bg-[#273219] text-white" : "bg-white text-[#484964]"} cursor-pointer text-center text-nowrap border border-[#dddddd] py-[6px] flex-1 rounded-[20px] text-[14px] leading-[20px] font-[500]`}
                                onClick={() => onClickSection(productRef)}
                            >
                                주문상품
                            </li>
                            <li
                                className={`${activeSection === "recipient" ? "bg-[#273219] text-white" : "bg-white text-[#484964]"} cursor-pointer text-center text-nowrap border border-[#dddddd] py-[6px] flex-1 rounded-[20px] text-[14px] leading-[20px] font-[500]`}
                                onClick={() => onClickSection(recipientRef)}
                            >
                                받으시는 분
                            </li>
                            <li
                                className={`${activeSection === "message" ? "bg-[#273219] text-white" : "bg-white text-[#484964]"} cursor-pointer text-center text-nowrap border border-[#dddddd] py-[6px] flex-1 rounded-[20px] text-[14px] leading-[20px] font-[500]`}
                                onClick={() => onClickSection(messageRef)}
                            >
                                전할 메시지
                            </li>
                            <li
                                className={`${activeSection === "delivery" ? "bg-[#273219] text-white" : "bg-white text-[#484964]"} cursor-pointer text-center text-nowrap border border-[#dddddd] py-[6px] flex-1 rounded-[20px] text-[14px] leading-[20px] font-[500]`}
                                onClick={() => onClickSection(deliveryRef)}
                            >
                                배송정보
                            </li>
                        </ul>
                    </div>

                    <form onSubmit={handleFormSubmit}>
                        <div className="flex-1 flex flex-col">
                            <div id="product" className="wrapper" style={{ scrollMarginTop: 130 }} ref={productRef}>
                                <details open className="group">
                                    <summary>
                                        <div className="flex justify-between items-center py-[16px]">
                                            <p className="font-bold">주문상품</p>
                                            <Arrow className="group-open:rotate-[-90deg] rotate-[90deg]" />
                                        </div>
                                    </summary>
                                    <div className="flex justify-between items-center pb-[16px]">
                                        {productInfo && (
                                            <SimpleOrderItem
                                                category={productInfo.product_category.category_name}
                                                name={productInfo.product_name}
                                                price={productInfo.product_amount}
                                                img={
                                                    productInfo?.product_image[0]?.file_name
                                                        ? `${enum_api_uri.api_uri}/${productInfo?.product_image[0].file_name}`
                                                        : ""
                                                }
                                                className="max-w-[75%]"
                                            />
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                update
                                                    ? navigate(`/orders/${params.idx}/update/select-category`)
                                                    : navigate(`/new-order/select-category`);
                                            }}
                                            className="text-nowrap text-[#484964] text-[14px] leading-[20px] border border-[#dddddd] rounded-[8px] px-[8px] py-[6px] font-[500]"
                                        >
                                            상품 찾기
                                        </button>
                                    </div>
                                </details>
                            </div>

                            <div
                                id="recipient"
                                className="wrapper border-t-[8px] border-[#F3F3F8]"
                                style={{ scrollMarginTop: 130 }}
                                ref={recipientRef}
                            >
                                <details open className="group">
                                    <summary>
                                        <div className="flex justify-between items-center py-[16px]">
                                            <div className="flex gap-[12px] items-center">
                                                <p className="font-bold">받으시는 분</p>
                                                <button
                                                    type="button"
                                                    className="text-[#484964] text-[14px] leading-[20px] rounded-[8px] px-[8px] py-[6px] border border-[#dddddd]"
                                                    onClick={() => {
                                                        setValues({
                                                            ...values,
                                                            recipient_name: formik.from.values.order_name,
                                                            recipient_mobile: formik.from.values.order_mobile,
                                                        });
                                                    }}
                                                >
                                                    주문자와 정보 동일
                                                </button>
                                            </div>
                                            <Arrow className="group-open:rotate-[-90deg] rotate-[90deg]" />
                                        </div>
                                    </summary>

                                    <div className="flex flex-col gap-[16px] pb-[16px]">
                                        <div className={`flex flex-col`}>
                                            <label htmlFor="recipient_name" className="mb-[4px] text-[#484964] leading-[24px]">
                                                받으시는 분 <span>*</span>
                                            </label>
                                            <InputField
                                                id="recipient_name"
                                                name="recipient_name"
                                                value={values.recipient_name}
                                                onChange={handleChange}
                                                placeholder="이름을 입력해주세요"
                                                error={touched.recipient_name && errorKeys.includes("recipient_name")}
                                                onClickClearButton={() => setFieldValue("recipient_name", "")}
                                                textMaxLength={20}
                                                autoComplete="off"
                                            />
                                            {touched.recipient_name && errors.recipient_name && (
                                                <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">
                                                    {errors.recipient_name}
                                                </div>
                                            )}
                                        </div>

                                        <div className={`flex flex-col`}>
                                            <label htmlFor="recipient_mobile" className="mb-[4px] text-[#484964] leading-[24px]">
                                                받으시는 분 연락처 <span>*</span>
                                            </label>
                                            <MobileInputField
                                                id="recipient_mobile"
                                                name="recipient_mobile"
                                                value={values.recipient_mobile}
                                                onChange={handleChange}
                                                placeholder="연락처를 입력해주세요"
                                                error={touched.recipient_mobile && errorKeys.includes("recipient_mobile")}
                                                onClickClearButton={() => setFieldValue("recipient_mobile", "")}
                                                autoComplete="off"
                                            />
                                            {touched.recipient_mobile && errors.recipient_mobile && (
                                                <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">
                                                    {errors.recipient_mobile}
                                                </div>
                                            )}
                                        </div>

                                        {/* 화환 옵션 설정 돼있을 시 */}
                                        {customerData?.data.product_category?.wreath_option ? (
                                            <React.Fragment>
                                                <div
                                                    className={`${customerData.data.event_content_option || customerData.data.related_person_option ? "flex flex-col gap-[4px]" : "hidden"}`}
                                                >
                                                    {/* 경조내용 노출 설정 */}
                                                    {customerData.data.event_content_option && (
                                                        <React.Fragment>
                                                            <label htmlFor="depth1_idx" className="text-[#484964] leading-[24px]">
                                                                경조내용 <span>*</span>
                                                            </label>
                                                            <div className={`flex gap-[4px] items-center`}>
                                                                <Sheet>
                                                                    <SheetTrigger
                                                                        asChild
                                                                        className="max-w-[120px]"
                                                                        ref={eventIdxSheetTiggerRef}
                                                                    >
                                                                        <div
                                                                            className={`overflow-hidden flex items-center rounded-xl border bg-white ${touched.depth1_idx && errorKeys.includes("depth1_idx") ? "[&:has(input)]:border-[#DC2626]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                                        >
                                                                            <input
                                                                                id="depth1_idx"
                                                                                name="depth1_idx"
                                                                                onChange={handleChange}
                                                                                value={values.depth1_idx ? event : ""}
                                                                                readOnly
                                                                                placeholder="선택"
                                                                                className="w-full cursor-pointer p-3 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0"
                                                                            />
                                                                            <SelectIcon className="shrink-0 mr-[10px]" />
                                                                        </div>
                                                                    </SheetTrigger>
                                                                    <SheetContent
                                                                        side="bottom"
                                                                        className="bg-white rounded-[15px_15px_0_0] max-w-[768px] mx-auto"
                                                                    >
                                                                        <SheetHeader>
                                                                            <SheetTitle hidden />
                                                                            <SheetDescription hidden />
                                                                            {eventData?.data && (
                                                                                <ul className="max-h-[50dvh] overflow-auto">
                                                                                    {eventData.data.flatMap(data => {
                                                                                        return (
                                                                                            <li
                                                                                                key={data.event_content_idx}
                                                                                                className={`${values.event_content_idx === data.event_content_idx ? "bg-[#F2F6EF] text-[#4B632B] font-bold text-[18px] leading-[28px]" : "text-[20px] leading-[30px] text-[#484964] font-[500]"} py-[12px] cursor-pointer`}
                                                                                                onClick={() => {
                                                                                                    setValues({
                                                                                                        ...values,
                                                                                                        depth1_idx: data.depth1_idx,
                                                                                                        event_content_idx:
                                                                                                            data.depth1_idx <= 2
                                                                                                                ? data.depth1_idx
                                                                                                                : values.event_content_idx,
                                                                                                        event_content: "",
                                                                                                    });

                                                                                                    eventIdxSheetTiggerRef.current?.click();
                                                                                                }}
                                                                                            >
                                                                                                {data.depth1}
                                                                                            </li>
                                                                                        );
                                                                                    })}
                                                                                </ul>
                                                                            )}
                                                                        </SheetHeader>
                                                                    </SheetContent>
                                                                </Sheet>

                                                                {/* 직접입력 */}
                                                                {values.depth1_idx === 2 ? (
                                                                    <InputField
                                                                        id="event_content"
                                                                        name="event_content"
                                                                        value={values.event_content}
                                                                        onChange={handleChange}
                                                                        placeholder="직접입력"
                                                                        onClickClearButton={() => setFieldValue("event_content", "")}
                                                                        ref={eventInputRef}
                                                                        textMaxLength={20}
                                                                    />
                                                                ) : (
                                                                    <Sheet>
                                                                        <SheetTrigger
                                                                            asChild
                                                                            className={`group flex-1 ${(!values.depth1_idx || values.depth1_idx === 1) && "pointer-events-none opacity-[0.4]"}`}
                                                                            ref={event2IdxSheetTiggerRef}
                                                                            disabled={!values.depth1_idx || values.depth1_idx === 1}
                                                                        >
                                                                            <div
                                                                                className={`overflow-hidden flex items-center justify-between rounded-xl border bg-white ${touched.event_content && errorKeys.includes("event_content") ? "[&:has(input)]:border-[#DC2626]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                                            >
                                                                                <input
                                                                                    id="event_content"
                                                                                    name="event_content"
                                                                                    value={values.event_content ?? ""}
                                                                                    onChange={handleChange}
                                                                                    readOnly
                                                                                    placeholder="선택"
                                                                                    className="w-full cursor-pointer p-3 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0"
                                                                                />
                                                                                <SelectIcon className="shrink-0 mr-[10px]" />
                                                                            </div>
                                                                        </SheetTrigger>
                                                                        <SheetContent
                                                                            side="bottom"
                                                                            className="bg-white rounded-[15px_15px_0_0] max-w-[768px] mx-auto"
                                                                        >
                                                                            <SheetHeader>
                                                                                <SheetTitle hidden />
                                                                                <SheetDescription hidden />
                                                                                {event2Data?.data && (
                                                                                    <ul className="max-h-[50dvh] overflow-auto">
                                                                                        {event2Data.data.flatMap(data => {
                                                                                            return (
                                                                                                <li
                                                                                                    key={data.event_content_idx}
                                                                                                    className={`${values.event_content === data.depth2 ? "bg-[#F2F6EF] text-[#4B632B] font-bold text-[18px] leading-[28px]" : "text-[20px] leading-[30px] text-[#484964] font-[500]"} py-[12px] cursor-pointer`}
                                                                                                    onClick={() => {
                                                                                                        setValues({
                                                                                                            ...values,
                                                                                                            event_content: data.depth2!,
                                                                                                            event_content_idx:
                                                                                                                data.event_content_idx,
                                                                                                        });

                                                                                                        event2IdxSheetTiggerRef.current?.click();
                                                                                                    }}
                                                                                                >
                                                                                                    {data.depth2}
                                                                                                </li>
                                                                                            );
                                                                                        })}
                                                                                    </ul>
                                                                                )}
                                                                            </SheetHeader>
                                                                        </SheetContent>
                                                                    </Sheet>
                                                                )}
                                                            </div>
                                                        </React.Fragment>
                                                    )}
                                                    {customerData.data.event_content_option && isErrorEventContent && (
                                                        <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">
                                                            {errors.depth1_idx ?? errors.event_content}
                                                        </div>
                                                    )}

                                                    {/* 대상자명(관계자명) 설정 */}
                                                    {customerData.data.related_person_option && (
                                                        <React.Fragment>
                                                            <label
                                                                htmlFor="related_person"
                                                                className="text-[#484964] leading-[24px] mt-[16px]"
                                                            >
                                                                대상자명
                                                            </label>
                                                            <InputField
                                                                id="related_person"
                                                                name="related_person"
                                                                value={values.related_person}
                                                                onChange={handleChange}
                                                                placeholder="대상자 이름을 입력해주세요(선택사항)"
                                                                onClickClearButton={() => setFieldValue("related_person", "")}
                                                                textMaxLength={20}
                                                            />
                                                        </React.Fragment>
                                                    )}
                                                </div>

                                                {/* 부서 노출 설정 */}
                                                {customerData?.data.department_option && (
                                                    <div className={`flex flex-col`}>
                                                        <label htmlFor="department" className="mb-[4px] text-[#484964] leading-[24px]">
                                                            부서
                                                        </label>
                                                        <InputField
                                                            id="department"
                                                            name="department"
                                                            value={values.department}
                                                            onChange={handleChange}
                                                            placeholder="부서를 입력해주세요"
                                                            onClickClearButton={() => setFieldValue("department", "")}
                                                            textMaxLength={20}
                                                        />
                                                    </div>
                                                )}

                                                {/* 사번 노출 설정 */}
                                                {customerData?.data.company_tel_option && (
                                                    <div className={`flex flex-col`}>
                                                        <label htmlFor="company_tel" className="mb-[4px] text-[#484964] leading-[24px]">
                                                            사번
                                                        </label>

                                                        <InputField
                                                            id="company_tel"
                                                            name="company_tel"
                                                            value={values.company_tel}
                                                            onChange={handleChange}
                                                            placeholder="사번을 입력해주세요"
                                                            onClickClearButton={() => setFieldValue("company_tel", "")}
                                                            textMaxLength={20}
                                                        />
                                                    </div>
                                                )}

                                                {/* 근무지 노출 설정 */}
                                                {customerData?.data.work_location_option && (
                                                    <div className={`flex flex-col`}>
                                                        <label htmlFor="work_location" className="mb-[4px] text-[#484964] leading-[24px]">
                                                            근무지
                                                        </label>
                                                        <InputField
                                                            id="work_location"
                                                            name="work_location"
                                                            value={values.work_location}
                                                            onChange={handleChange}
                                                            placeholder="근무지를 입력해주세요"
                                                            onClickClearButton={() => setFieldValue("work_location", "")}
                                                            textMaxLength={20}
                                                        />
                                                    </div>
                                                )}

                                                {/* 노조 노출 설정 */}
                                                {customerData?.data.union_option && (
                                                    <div className={`flex flex-col`}>
                                                        <label htmlFor="is_union" className="mb-[4px] text-[#484964] leading-[24px]">
                                                            노조가입 여부 <span>*</span>
                                                        </label>
                                                        <div
                                                            className={`p-[12px] flex gap-[8px] rounded-xl border overflow-hidden [&:has(input:checked)]:border-[#4B632B]`}
                                                        >
                                                            <Checkbox
                                                                id="is_union"
                                                                name="is_union"
                                                                type="round"
                                                                checked={values.is_union}
                                                                readOnly
                                                            />

                                                            <label htmlFor="is_union">{values.is_union ? "가입" : "미가입"}</label>
                                                        </div>
                                                        <div className="bg-[#F8F9F7] mt-[8px] text-[#4B632B] rounded-[12px] py-[16px] text-center font-[500]">
                                                            {values.is_union
                                                                ? "현재 노조가입 필수 상품을 선택하셨습니다."
                                                                : "현재 노조 미가입 상품을 선택하셨습니다."}
                                                        </div>
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        ) : null}
                                    </div>
                                </details>
                            </div>

                            <div
                                id="message"
                                className="wrapper border-t-[8px] border-[#F3F3F8]"
                                style={{ scrollMarginTop: 130 }}
                                ref={messageRef}
                            >
                                <details open className="group">
                                    <summary>
                                        <div className="flex justify-between items-center py-[16px]">
                                            <p className="font-bold">전할 메시지</p>

                                            <Arrow className="group-open:rotate-[-90deg] rotate-[90deg]" />
                                        </div>
                                    </summary>

                                    <div className="flex flex-col gap-[16px] pb-[16px]">
                                        {/* 화환 옵션 설정 돼있을 시 */}
                                        {customerData?.data.product_category?.wreath_option ? (
                                            <>
                                                {/* 개인 회원 일 때 */}
                                                {/* {isIndividualCustomer && (
                                                    <div className="flex flex-col gap-[16px]">
                                                        {customerData?.data.wreath_sender_option && (
                                                            <div className="flex flex-col gap-[4px]">
                                                                <label htmlFor="wreath_sender" className="text-[#484964] leading-[24px]">
                                                                    명의
                                                                </label>

                                                                <div className="flex gap-[4px]">
                                                                    <InputField
                                                                        id="wreath_sender"
                                                                        name="wreath_sender"
                                                                        value={values.wreath_sender}
                                                                        onChange={handleChange}
                                                                        placeholder="직접 입력하거나 검색하세요"
                                                                        onClickClearButton={() => setFieldValue("wreath_sender", "")}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="rounded-[12px] border border-[#4B632B] text-[#4B632B] flex gap-[10px] items-center p-[12px] [&_path]:stroke-[#4B632B] [&_path:nth-child(2)]:stroke-[#4B632B]"
                                                                        onClick={() =>
                                                                            openOverlay(
                                                                                <NameSearchOverlay
                                                                                    customerIdx={customer_idx!}
                                                                                    onClickClose={closeOverlay}
                                                                                    onClickTarget={({ name, idx }) => {
                                                                                        setFieldValue("wreath_sender", name);
                                                                                        closeOverlay();
                                                                                    }}
                                                                                />,
                                                                            )
                                                                        }
                                                                    >
                                                                        검색
                                                                        <SearchIcon />
                                                                    </button>
                                                                </div>

                                                                <SelectBox
                                                                    height="max-h-64"
                                                                    placeholder="직접 입력하거나 선택하세요"
                                                                    onSearchChange={value => setFieldValue("wreath_sender", value)}
                                                                    // searchable
                                                                    options={
                                                                        values.wreath_sender_options
                                                                            ? [
                                                                                  { value: "custom", label: "직접입력" },
                                                                                  ...values.wreath_sender_options.map(sender => ({
                                                                                      value: sender.value,
                                                                                      label: sender.value,
                                                                                  })),
                                                                              ]
                                                                            : [{ value: "custom", label: "직접입력" }]
                                                                    }
                                                                    full
                                                                    className={`w-full [&>div]:rounded-xl [&>div:first-child]:h-[53px] [&>div]:border-[#e5e7eb] [&_span]:text-lg ${(selectedWreathSenderType === "custom" || (selectedWreathSenderType === "select" && values?.wreath_sender?.length === 0)) && "[&_span]:text-[#ddd]"}`}
                                                                    value={values.wreath_sender ?? ""}
                                                                    onChange={value => {
                                                                        if (value === "custom") {
                                                                            setSelectedWreathSenderType(value);
                                                                            setFieldValue("wreath_sender", "");
                                                                        } else {
                                                                            const selectedWord = values.wreath_sender_options.find(
                                                                                sender => sender.value === value,
                                                                            );
                                                                            if (selectedWord) {
                                                                                setSelectedWreathSenderType("select");
                                                                                setFieldValue("wreath_sender", selectedWord.value);
                                                                            }
                                                                        }
                                                                    }}
                                                                />

                                                                {selectedWreathSenderType === "custom" && (
                                                                    <InputField
                                                                        id="wreath_sender"
                                                                        name="wreath_sender"
                                                                        value={values.wreath_sender}
                                                                        onChange={handleChange}
                                                                        placeholder="직접 입력"
                                                                        onClickClearButton={() => setFieldValue("wreath_sender", "")}
                                                                    />
                                                                )}
                                                            </div>
                                                        )}

                                                        {customerData?.data.wreath_word_option && (
                                                            <div className="flex flex-col gap-[4px]">
                                                                <label htmlFor="wreath_word" className="text-[#484964] leading-[24px]">
                                                                    경조사어
                                                                </label>
                                                                <div className="flex gap-[4px]">
                                                                    <InputField
                                                                        id="wreath_word"
                                                                        name="wreath_word"
                                                                        value={values.wreath_word}
                                                                        onChange={handleChange}
                                                                        placeholder="직접 입력하거나 검색하세요"
                                                                        onClickClearButton={() => setFieldValue("wreath_word", "")}
                                                                    />

                                                                    <button
                                                                        type="button"
                                                                        className="rounded-[12px] border border-[#4B632B] text-[#4B632B] flex gap-[10px] items-center p-[12px] [&_path]:stroke-[#4B632B] [&_path:nth-child(2)]:stroke-[#4B632B]"
                                                                        onClick={() =>
                                                                            openOverlay(
                                                                                <KeywordSearchOverlay
                                                                                    onClickClose={closeOverlay}
                                                                                    onClickTarget={({ name, idx }) => {
                                                                                        setFieldValue("wreath_word", name);
                                                                                        closeOverlay();
                                                                                    }}
                                                                                />,
                                                                            )
                                                                        }
                                                                    >
                                                                        검색
                                                                        <SearchIcon />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )} */}
                                                {/* 회원 or 비회원 일 때 */}
                                                {/* {!isIndividualCustomer && ( */}
                                                <div className="flex flex-col gap-[16px]">
                                                    {/* 노조 가입 */}
                                                    {values.is_union && (
                                                        <div className="flex flex-col gap-[16px]">
                                                            {customerData?.data.wreath_sender_option && (
                                                                <div className="flex flex-col gap-[4px]">
                                                                    <label
                                                                        htmlFor="wreath_sender"
                                                                        className="text-[#484964] leading-[24px]"
                                                                    >
                                                                        명의
                                                                    </label>

                                                                    <InputField
                                                                        id="wreath_sender"
                                                                        name="wreath_sender"
                                                                        value={values.wreath_sender}
                                                                        onChange={handleChange}
                                                                        placeholder={
                                                                            values.wreath_sender ? "직접 입력하거나 검색하세요" : ""
                                                                        }
                                                                        onClickClearButton={() => {}} // 수정 불가능
                                                                        disabled
                                                                    />
                                                                </div>
                                                            )}

                                                            {customerData?.data.wreath_word_option && (
                                                                <div>
                                                                    {values.wreath_word ? (
                                                                        <div className="bg-[#F8F9F7] text-[#4B632B] rounded-[12px] py-[16px] px-[12px] text-left font-[500]">
                                                                            {values.wreath_word}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="bg-[#F8F9F7] text-[#4B632B] rounded-[12px] py-[26px] px-[12px] text-left font-[500]"></div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* 노조 미가입 */}
                                                    {!values.is_union && (
                                                        <div className="flex flex-col gap-[16px]">
                                                            {customerData?.data.wreath_sender_option && (
                                                                <div className="flex flex-col gap-[4px]">
                                                                    <label
                                                                        htmlFor="wreath_sender"
                                                                        className="text-[#484964] leading-[24px]"
                                                                    >
                                                                        명의
                                                                    </label>
                                                                    <div className="flex gap-[4px]">
                                                                        <InputField
                                                                            id="wreath_sender"
                                                                            name="wreath_sender"
                                                                            value={values.wreath_sender}
                                                                            onChange={handleChange}
                                                                            placeholder="직접 입력하거나 검색하세요"
                                                                            onClickClearButton={() => setFieldValue("wreath_sender", "")}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="rounded-[12px] border border-[#4B632B] text-[#4B632B] flex gap-[10px] items-center p-[12px] [&_path]:stroke-[#4B632B] [&_path:nth-child(2)]:stroke-[#4B632B]"
                                                                            onClick={() =>
                                                                                openOverlay(
                                                                                    <NameSearchOverlay
                                                                                        customerIdx={customer_idx!}
                                                                                        onClickClose={closeOverlay}
                                                                                        onClickTarget={({ name, idx }) => {
                                                                                            setFieldValue("wreath_sender", name);
                                                                                            closeOverlay();
                                                                                        }}
                                                                                    />,
                                                                                )
                                                                            }
                                                                        >
                                                                            검색
                                                                            <SearchIcon />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {customerData?.data.wreath_word_option && (
                                                                <div className="flex flex-col gap-[4px]">
                                                                    <label htmlFor="wreath_word" className="text-[#484964] leading-[24px]">
                                                                        경조사어
                                                                    </label>
                                                                    <div className="flex gap-[4px]">
                                                                        <InputField
                                                                            id="wreath_word"
                                                                            name="wreath_word"
                                                                            value={values.wreath_word}
                                                                            onChange={handleChange}
                                                                            placeholder="직접 입력하거나 검색하세요"
                                                                            onClickClearButton={() => setFieldValue("wreath_word", "")}
                                                                        />

                                                                        <button
                                                                            type="button"
                                                                            className="rounded-[12px] border border-[#4B632B] text-[#4B632B] flex gap-[10px] items-center p-[12px] [&_path]:stroke-[#4B632B] [&_path:nth-child(2)]:stroke-[#4B632B]"
                                                                            onClick={() =>
                                                                                openOverlay(
                                                                                    <KeywordSearchOverlay
                                                                                        onClickClose={closeOverlay}
                                                                                        onClickTarget={({ name, idx }) => {
                                                                                            setFieldValue("wreath_word", name);
                                                                                            closeOverlay();
                                                                                        }}
                                                                                    />,
                                                                                )
                                                                            }
                                                                        >
                                                                            검색
                                                                            <SearchIcon />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                {/* )} */}
                                            </>
                                        ) : null}
                                    </div>
                                </details>
                            </div>

                            <div
                                id="delivery"
                                className="wrapper border-t-[8px] border-[#F3F3F8]"
                                style={{ scrollMarginTop: 130 }}
                                ref={deliveryRef}
                            >
                                <details open className="group">
                                    <summary>
                                        <div className="flex justify-between items-center py-[16px]">
                                            <p className="font-bold">배송 정보</p>
                                            <Arrow className="group-open:rotate-[-90deg] rotate-[90deg]" />
                                        </div>
                                    </summary>

                                    <div className="flex flex-col gap-[16px] pb-[16px]">
                                        <div className="flex gap-[8px] items-center p-[4px]">
                                            <Checkbox
                                                type="square"
                                                id="delivery_immediate"
                                                checked={values.delivery_immediate}
                                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                    const checked = event.target.checked;
                                                    const now = new Date();
                                                    if (checked) {
                                                        setValues({
                                                            ...values,
                                                            delivery_immediate: checked,
                                                            delivery_hour: "",
                                                            delivery_minute: "",
                                                            delivery_type: "",
                                                            delivery_date: format(now, "yyyy-MM-dd"),
                                                        });
                                                        setPickerDay(now);
                                                    } else {
                                                        setValues({
                                                            ...values,
                                                            delivery_immediate: checked,
                                                            delivery_hour: update ? values.original_delivery_hour : values.delivery_minute,
                                                            delivery_minute: update
                                                                ? values.original_delivery_minute
                                                                : values.delivery_minute,
                                                            delivery_type: update ? values.original_delivery_type : values.delivery_type,
                                                            delivery_date: update ? values.original_delivery_date : "",
                                                        });
                                                        if (update) {
                                                            setPickerDay(
                                                                new Date(formatDisplayDateStringToDate(values.original_delivery_date)),
                                                            );
                                                        } else {
                                                            setPickerDay(now);
                                                        }
                                                        // setFieldValue("delivery_immediate", checked);
                                                    }
                                                }}
                                            />
                                            <label htmlFor="delivery_immediate" className="font-[500]">
                                                체크시, 지금 즉시 배송
                                            </label>
                                        </div>

                                        <div className="flex flex-col gap-[4px]">
                                            <label htmlFor="delivery_date" className="text-[#484964] leading-[24px]">
                                                배송일 <span>*</span>
                                            </label>

                                            <Sheet
                                                onOpenChange={open => {
                                                    if (!open) {
                                                        // 선택 안하고 모달 닫았을 때
                                                        if (values.delivery_date === "") {
                                                            setPickerDay(new Date());
                                                            return;
                                                        }

                                                        if (formatDeliveryDate(pickerDay) !== values.delivery_date) {
                                                            setPickerDay(new Date(formatDisplayDateStringToDate(values.delivery_date)));
                                                        }
                                                    } else {
                                                        if (values.delivery_date) {
                                                            setPickerDay(new Date(formatDisplayDateStringToDate(values.delivery_date)));
                                                        }
                                                    }
                                                }}
                                            >
                                                <SheetTrigger
                                                    asChild
                                                    className="w-full data-[disabled='true']:opacity-[0.4] data-[disabled='true']:pointer-events-none"
                                                    ref={datePickerSheetTriggerRef}
                                                    data-disabled={values.delivery_immediate}
                                                >
                                                    <div
                                                        className={`overflow-hidden flex items-center justify-between rounded-xl border bg-white pr-[20px] ${touched.delivery_date && errorKeys.includes("delivery_date") ? "[&:has(input)]:border-[#DC2626]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                    >
                                                        <input
                                                            id="delivery_date"
                                                            name="delivery_date"
                                                            value={formatDisplayDeliveryDate(values.delivery_date)}
                                                            // value={values.delivery_date}
                                                            onChange={formik.to.handleChange}
                                                            readOnly
                                                            placeholder="배송일"
                                                            className="cursor-pointer w-full p-3 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0"
                                                        />
                                                        <CalendarIcon />
                                                    </div>
                                                </SheetTrigger>
                                                <SheetContent
                                                    side="bottom"
                                                    className="bg-white rounded-[15px_15px_0_0] max-w-[768px] mx-auto"
                                                >
                                                    <SheetHeader>
                                                        <SheetTitle className="font-[500] text-[20px] leading-[30px]" hidden></SheetTitle>
                                                        <SheetDescription hidden />
                                                        <div className="inline-block mx-auto">
                                                            <DayPicker
                                                                mode="single"
                                                                selected={pickerDay}
                                                                onSelect={date => {
                                                                    if (date) {
                                                                        setPickerDay(date);
                                                                    }
                                                                }}
                                                                defaultMonth={pickerDay}
                                                                disabled={pickerDay ? { before: new Date() } : undefined}
                                                                locale={ko} // 한국어 로케일 설정
                                                                formatters={{
                                                                    formatCaption: month => format(month, "yyyy년 MM월", { locale: ko }), // 2024년 12월 형식으로 표시
                                                                }}
                                                            />
                                                        </div>
                                                        <div>
                                                            <button
                                                                type="button"
                                                                className="bg-[#273219] text-white font-bold text-[18px] leading-[28px] py-[12px] rounded-[12px] w-full"
                                                                onClick={() => {
                                                                    if (pickerDay) {
                                                                        const date = format(pickerDay, "yyyy-MM-dd");
                                                                        setFieldValue("delivery_date", date);
                                                                        // const date = formatDeliveryDate(pickerDay);
                                                                        // setFieldValue("delivery_date", date);
                                                                    }
                                                                    datePickerSheetTriggerRef.current?.click();
                                                                }}
                                                            >
                                                                {format(pickerDay, "yyyy년 MM월 dd일", { locale: ko })} 선택
                                                            </button>
                                                        </div>
                                                    </SheetHeader>
                                                </SheetContent>
                                            </Sheet>

                                            {touched.delivery_date && errors.delivery_date && (
                                                <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">
                                                    {errors.delivery_date}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-[4px]">
                                            <label htmlFor="delivery_hour" className="text-[#484964] leading-[24px]">
                                                배송시간 <span>*</span>
                                            </label>

                                            <div className={`flex gap-[8px] items-center`}>
                                                <Sheet>
                                                    <SheetTrigger asChild className="flex-1" ref={deliveryHourSheetTiggerRef}>
                                                        <div
                                                            className={`overflow-hidden flex items-center rounded-xl border bg-white [&:has(input:disabled)]:bg-[#F3F3F8] ${touched.delivery_hour && errorKeys.includes("delivery_hour") ? "[&:has(input)]:border-[#DC2626]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                        >
                                                            <input
                                                                id="delivery_hour"
                                                                name="delivery_hour"
                                                                value={values.delivery_hour}
                                                                onChange={handleChange}
                                                                readOnly
                                                                placeholder="시"
                                                                disabled={values.delivery_immediate}
                                                                className="cursor-pointer w-full p-3 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0 disabled:bg-[#F3F3F8]"
                                                            />
                                                            <SelectIcon className="shrink-0 mr-[10px]" />
                                                        </div>
                                                    </SheetTrigger>
                                                    <SheetContent
                                                        side="bottom"
                                                        className="bg-white rounded-[15px_15px_0_0] max-w-[768px] mx-auto"
                                                    >
                                                        <SheetHeader>
                                                            <SheetTitle className="font-[500] text-[20px] leading-[30px]">
                                                                배송시간 &rsaquo; 시
                                                            </SheetTitle>
                                                            <SheetDescription hidden />

                                                            <ul className="max-h-[50dvh] overflow-auto">
                                                                {deliveryHour.map((hour, index) => {
                                                                    return (
                                                                        <li
                                                                            key={index}
                                                                            className={`${values.delivery_hour === hour ? "bg-[#F2F6EF] text-[#4B632B] font-bold text-[18px] leading-[28px]" : "text-[20px] leading-[30px] text-[#484964] font-[500]"} py-[12px] cursor-pointer`}
                                                                            onClick={() => {
                                                                                if (
                                                                                    hour === "오전중" ||
                                                                                    hour === "오후중" ||
                                                                                    hour === "당일배송"
                                                                                ) {
                                                                                    setValues({
                                                                                        ...values,
                                                                                        delivery_hour: hour,
                                                                                        delivery_minute: "",
                                                                                        delivery_type: "",
                                                                                    });
                                                                                } else {
                                                                                    setFieldValue("delivery_hour", hour);
                                                                                }
                                                                                deliveryHourSheetTiggerRef.current?.click();
                                                                            }}
                                                                        >
                                                                            {hour}
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </SheetHeader>
                                                    </SheetContent>
                                                </Sheet>
                                                <Sheet>
                                                    <SheetTrigger
                                                        disabled={
                                                            values.delivery_hour === "오전중" ||
                                                            values.delivery_hour === "오후중" ||
                                                            values.delivery_hour === "당일배송"
                                                        }
                                                        asChild
                                                        className="flex-1"
                                                        ref={deliveryMinuteSheetTiggerRef}
                                                    >
                                                        <button
                                                            type="button"
                                                            className={`group overflow-hidden flex items-center justify-between rounded-xl border bg-white [&:has(input:disabled)]:bg-[#F3F3F8] disabled:pointer-events-none disabled:bg-[#F3F3F8] ${!isSpecialDeliveryHour && touched.delivery_minute && errorKeys.includes("delivery_minute") ? "[&:has(input)]:border-[#DC2626]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                        >
                                                            <input
                                                                id="delivery_minute"
                                                                name="delivery_minute"
                                                                value={values.delivery_minute}
                                                                onChange={handleChange}
                                                                readOnly
                                                                placeholder="분"
                                                                disabled={values.delivery_immediate}
                                                                className="w-full cursor-pointer p-3 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0 disabled:bg-[#F3F3F8] group-disabled:bg-[#F3F3F8]"
                                                            />
                                                            <SelectIcon className="shrink-0 mr-[10px]" />
                                                        </button>
                                                    </SheetTrigger>
                                                    <SheetContent
                                                        side="bottom"
                                                        className="bg-white rounded-[15px_15px_0_0] max-w-[768px] mx-auto"
                                                    >
                                                        <SheetHeader>
                                                            <SheetTitle className="font-[500] text-[20px] leading-[30px]">
                                                                배송시간 &rsaquo; 분
                                                            </SheetTitle>
                                                            <SheetDescription hidden />
                                                            <ul className="max-h-[50dvh] overflow-auto">
                                                                {deliveryMinute.map((minute, index) => {
                                                                    return (
                                                                        <li
                                                                            key={index}
                                                                            className={`${values.delivery_minute === minute ? "bg-[#F2F6EF] text-[#4B632B] font-bold text-[18px] leading-[28px]" : "text-[20px] leading-[30px] text-[#484964] font-[500]"} py-[12px] cursor-pointer`}
                                                                            onClick={() => {
                                                                                let isValidHour = false;
                                                                                if (values.delivery_hour) {
                                                                                    isValidHour = true;
                                                                                } else {
                                                                                    isValidHour = false;
                                                                                }
                                                                                if (isValidHour) {
                                                                                    setValues({
                                                                                        ...values,
                                                                                        delivery_type: "까지",
                                                                                        delivery_minute: minute,
                                                                                    });
                                                                                } else {
                                                                                    setFieldValue("delivery_minute", minute);
                                                                                }
                                                                                deliveryMinuteSheetTiggerRef.current?.click();
                                                                            }}
                                                                        >
                                                                            {minute}
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </SheetHeader>
                                                    </SheetContent>
                                                </Sheet>
                                            </div>

                                            <Sheet>
                                                <SheetTrigger
                                                    asChild
                                                    disabled={
                                                        values.delivery_hour === "오전중" ||
                                                        values.delivery_hour === "오후중" ||
                                                        values.delivery_hour === "당일배송"
                                                    }
                                                    className="flex-1"
                                                    ref={deliveryTypeSheetTiggerRef}
                                                >
                                                    <button
                                                        type="button"
                                                        className={`group overflow-hidden flex items-center justify-between rounded-xl border bg-white [&:has(input:disabled)]:bg-[#F3F3F8] disabled:pointer-events-none disabled:bg-[#F3F3F8] ${!isSpecialDeliveryHour && touched.delivery_type && errorKeys.includes("delivery_type") ? "[&:has(input)]:border-[#DC2626]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                    >
                                                        <input
                                                            id="delivery_type"
                                                            name="delivery_type"
                                                            value={values.delivery_type}
                                                            onChange={handleChange}
                                                            readOnly
                                                            placeholder="선택"
                                                            disabled={values.delivery_immediate}
                                                            className="w-full cursor-pointer p-3 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0 disabled:bg-[#F3F3F8] group-disabled:bg-[#F3F3F8]"
                                                        />
                                                        <SelectIcon className="shrink-0 mr-[10px]" />
                                                    </button>
                                                </SheetTrigger>
                                                <SheetContent
                                                    side="bottom"
                                                    className="bg-white rounded-[15px_15px_0_0] max-w-[768px] mx-auto"
                                                >
                                                    <SheetHeader>
                                                        <SheetTitle className="font-[500] text-[20px] leading-[30px]">
                                                            배송시간 &rsaquo; 선택
                                                        </SheetTitle>
                                                        <SheetDescription hidden />
                                                        <ul className="max-h-[50dvh] overflow-auto">
                                                            {deliveryType.map((type, index) => {
                                                                return (
                                                                    <li
                                                                        key={index}
                                                                        className={`${values.delivery_type === type ? "bg-[#F2F6EF] text-[#4B632B] font-bold text-[18px] leading-[28px]" : "text-[20px] leading-[30px] text-[#484964] font-[500]"} py-[12px] cursor-pointer`}
                                                                        onClick={() => {
                                                                            setFieldValue("delivery_type", type);
                                                                            deliveryTypeSheetTiggerRef.current?.click();
                                                                        }}
                                                                    >
                                                                        {type}
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </SheetHeader>
                                                </SheetContent>
                                            </Sheet>

                                            {isErrorDeliveryTime && (
                                                <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">
                                                    {errors.delivery_time}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <div className="flex flex-col gap-[4px]">
                                                <label htmlFor="delivery_address_detail" className="text-[#484964] leading-[24px]">
                                                    배송주소 <span>*</span>
                                                </label>
                                                <div className={`flex gap-[4px] items-center`}>
                                                    <InputField
                                                        id="delivery_post"
                                                        name="delivery_post"
                                                        value={values.delivery_post}
                                                        onChange={handleChange}
                                                        placeholder="우편번호"
                                                        error={touched.delivery_post && errorKeys.includes("delivery_post")}
                                                        onClickClearButton={() => setFieldValue("delivery_post", "")}
                                                        textMaxLength={5}
                                                        autoComplete="off"
                                                    />

                                                    <Sheet>
                                                        <SheetTrigger asChild ref={addressSheetTriggerRef}>
                                                            <button
                                                                type="button"
                                                                className="text-nowrap py-[12px] px-[24px] text-[18px] leading-[28px] text-[#4B632B] border border-[#4B632B] rounded-[12px]"
                                                            >
                                                                우편번호 검색
                                                            </button>
                                                        </SheetTrigger>
                                                        <SheetContent
                                                            side="bottom"
                                                            className="bg-white rounded-[15px_15px_0_0] max-w-[768px] mx-auto [&>div>div]:m-0 [&>div>div]:w-full [&>div>div]:flex [&>div>div]:justify-center"
                                                        >
                                                            <SheetHeader>
                                                                <SheetTitle hidden />
                                                                <SheetDescription hidden />
                                                                <div className="inline-block mx-auto">
                                                                    <DaumPostcodeEmbed
                                                                        theme={themeObj}
                                                                        style={postCodeStyle}
                                                                        onComplete={data => completeHandler(data)}
                                                                        onClose={closeHandler}
                                                                    />
                                                                </div>
                                                            </SheetHeader>
                                                        </SheetContent>
                                                    </Sheet>
                                                </div>
                                                <InputField
                                                    id="delivery_address"
                                                    name="delivery_address"
                                                    value={values.delivery_address}
                                                    onChange={handleChange}
                                                    placeholder="주소"
                                                    error={touched.delivery_address && errorKeys.includes("delivery_address")}
                                                    onClickClearButton={() => setFieldValue("delivery_address", "")}
                                                    textMaxLength={70}
                                                    autoComplete="off"
                                                />
                                                <InputField
                                                    id="delivery_address_detail"
                                                    name="delivery_address_detail"
                                                    value={values.delivery_address_detail}
                                                    onChange={handleChange}
                                                    placeholder="상세주소"
                                                    className="w-full"
                                                    onClickClearButton={() => setFieldValue("delivery_address_detail", "")}
                                                    textMaxLength={70}
                                                    autoComplete="off"
                                                />
                                            </div>

                                            {touched.delivery_post && errors.delivery_post && (
                                                <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">
                                                    {errors.delivery_post}
                                                </div>
                                            )}
                                            {touched.delivery_address && errors.delivery_address && (
                                                <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">
                                                    {errors.delivery_address}
                                                </div>
                                            )}
                                        </div>

                                        <div className={`flex flex-col gap-[4px]`}>
                                            <label htmlFor="delivery_memo_idx" className="text-[#484964] leading-[24px]">
                                                배송메모
                                            </label>

                                            <Sheet>
                                                <SheetTrigger asChild className="w-full" ref={sheetTriggerRef}>
                                                    <div
                                                        className={`overflow-hidden flex items-center justify-between rounded-xl border bg-white pr-[20px] `}
                                                    >
                                                        <input
                                                            id="delivery_memo_idx"
                                                            name="delivery_memo_idx"
                                                            value={targetDeliveryMemo ?? ""}
                                                            onChange={handleChange}
                                                            placeholder="선택"
                                                            readOnly
                                                            className="cursor-pointer w-full p-3 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0"
                                                        />
                                                        <SelectIcon />
                                                    </div>
                                                </SheetTrigger>
                                                <SheetContent
                                                    side="bottom"
                                                    className="bg-white rounded-[15px_15px_0_0] max-w-[768px] mx-auto"
                                                >
                                                    <SheetHeader>
                                                        <SheetTitle className="font-[500] text-[20px] leading-[30px] text-left">
                                                            배송메모
                                                        </SheetTitle>
                                                        <SheetDescription hidden />
                                                        {deliveryMemo && (
                                                            <ul className="max-h-[50dvh] overflow-auto">
                                                                {deliveryMemo.data.map(item => {
                                                                    return (
                                                                        <li
                                                                            key={item.delivery_memo_idx}
                                                                            className={`${values.delivery_memo_idx === item.delivery_memo_idx ? "bg-[#F2F6EF] text-[#4B632B] font-bold text-[18px] leading-[28px]" : "text-[20px] leading-[30px] text-[#484964] font-[500]"} py-[12px] cursor-pointer`}
                                                                            onClick={() => {
                                                                                setValues({
                                                                                    ...values,
                                                                                    delivery_memo_idx: item.delivery_memo_idx,
                                                                                    delivery_memo: item.delivery_memo,
                                                                                    display_delivery_memo: "",
                                                                                });
                                                                                sheetTriggerRef.current?.click();
                                                                            }}
                                                                        >
                                                                            {item.delivery_memo}
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        )}
                                                    </SheetHeader>
                                                </SheetContent>
                                            </Sheet>

                                            <InputField
                                                id="display_delivery_memo"
                                                name="display_delivery_memo"
                                                value={values.display_delivery_memo}
                                                onChange={handleChange}
                                                placeholder="직접입력"
                                                disabled={values.delivery_memo_idx !== 1}
                                                onClickClearButton={() => setFieldValue("display_delivery_memo", "")}
                                                className="disabled:bg-[#F3F3F8]"
                                                wrapperClassName="[&:has(input:disabled)]:bg-[#F3F3F8]"
                                                textMaxLength={30}
                                            />
                                        </div>

                                        <div className={`flex flex-col gap-[4px]`}>
                                            <label htmlFor="file" className="text-[#484964] leading-[24px]">
                                                초대장, 청첩장, 부고장 정보 (최대 4장)
                                            </label>

                                            <div className="relative">
                                                <input
                                                    type="file"
                                                    id="file"
                                                    name="file"
                                                    multiple
                                                    accept={allowedMimeTypes.toString()}
                                                    title={inputTooltipDescription}
                                                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                        const { files } = event.target;

                                                        if (files && files.length > 0) {
                                                            // 유효한 파일인지 확인
                                                            const invalidFiles = Array.from(files).filter(
                                                                file => !allowedMimeTypes.includes(file.type),
                                                            );
                                                            if (invalidFiles.length > 0) {
                                                                openOverlay(
                                                                    <SystemPopup
                                                                        message="등록 할 수 없는 파일 형식 입니다."
                                                                        onClickClose={closeOverlay}
                                                                    />,
                                                                );
                                                                event.target.value = "";
                                                                return;
                                                            }
                                                            handleChangeFile(files);
                                                        }
                                                        event.target.value = ""; //input value 초기화
                                                    }}
                                                    className="absolute inset-0 opacity-0 disabled:cursor-not-allowed"
                                                />

                                                <div className="flex items-center gap-[10px] justify-center text-[18px] leading-[28px] font-[500] text-[#4B632B] border border-[#4B632B] rounded-[12px] py-[12px]">
                                                    <FileUploadIcon />
                                                    이미지 첨부
                                                </div>
                                            </div>

                                            <ul className="flex gap-[4px]">
                                                {/* 수정 모드시 기존 이미지 */}
                                                {update &&
                                                    fileState?.file.map((file: OrderFileProps | File, index) => {
                                                        if (file instanceof File) {
                                                            const key = file.name + index;
                                                            return (
                                                                <li
                                                                    key={key}
                                                                    className="w-[60px] h-[60px] overflow-hidden flex justift-center items-center rounded-[8px] bg-[#eee] relative"
                                                                >
                                                                    <img
                                                                        src={URL.createObjectURL(file)}
                                                                        alt={file.name}
                                                                        className="w-full h-full object-fit"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteFile(file)}
                                                                        className="absolute right-0 top-0 bg-[rgba(0,0,0,0.6)] w-[20px] h-[20px] flex justify-center items-center"
                                                                    >
                                                                        <CloseWhite className="[&_path]:fill-white" width={8} height={8} />
                                                                        <span className="inline-block indent-[-9999em]">이미지 삭제</span>
                                                                    </button>
                                                                </li>
                                                            );
                                                        } else {
                                                            return (
                                                                <li
                                                                    key={file.file_idx}
                                                                    className="w-[60px] h-[60px] overflow-hidden flex justift-center items-center rounded-[8px] bg-[#eee] relative"
                                                                >
                                                                    <img
                                                                        src={`${enum_api_uri.api_uri}/${file.file_name}`}
                                                                        alt={file.file_name}
                                                                        className="w-full h-full object-fit"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteFile(file)}
                                                                        className="absolute right-0 top-0 bg-[rgba(0,0,0,0.6)] w-[20px] h-[20px] flex justify-center items-center"
                                                                    >
                                                                        <CloseWhite className="[&_path]:fill-white" width={8} height={8} />
                                                                        <span className="inline-block indent-[-9999em]">이미지 삭제</span>
                                                                    </button>
                                                                </li>
                                                            );
                                                        }
                                                    })}

                                                {!update &&
                                                    fileState?.file.map((file: File, index: number) => {
                                                        const key = file.name + index;
                                                        return (
                                                            <li
                                                                key={key}
                                                                className="w-[60px] h-[60px] overflow-hidden flex justift-center items-center rounded-[8px] bg-[#eee] relative"
                                                            >
                                                                <img
                                                                    src={URL.createObjectURL(file)}
                                                                    alt={file.name}
                                                                    className="w-full h-full object-fit"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeleteFile(file)}
                                                                    className="absolute right-0 top-0 bg-[rgba(0,0,0,0.6)] w-[20px] h-[20px] flex justify-center items-center"
                                                                >
                                                                    <CloseWhite className="[&_path]:fill-white" width={8} height={8} />
                                                                    <span className="inline-block indent-[-9999em]">이미지 삭제</span>
                                                                </button>
                                                            </li>
                                                        );
                                                    })}
                                            </ul>

                                            <TextareaField
                                                value={values.file_memo}
                                                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                                                    const value = event.target.value;
                                                    setFieldValue("file_memo", value);
                                                }}
                                                placeholder={`직접입력

1.링크첨부
2.결제정보 (0000-0000-0000-0000 00/00)`}
                                            />
                                        </div>
                                    </div>
                                </details>
                            </div>

                            <div className="wrapper py-[12px]">
                                <p className="text-[#484964] text-[14px] leadinf-[20px] text-center mb-[8px]">
                                    주문 내용을 모두 확인하였으며, 정보 제공에 동의합니다.
                                </p>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    data-disabled={isSubmitting}
                                    className="bg-[#273219] text-white text-[18px] leading-[28px] font-bold py-[12px] rounded-[12px] w-full data-[disabled='true']:opacity-[0.4] disabled:cursor-not-allowed"
                                >
                                    {isSubmitting
                                        ? update
                                            ? "주문 수정 중..."
                                            : "주문 접수 중..."
                                        : update
                                          ? "주문수정하기"
                                          : "주문신청하기"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </OverlayWrapper>
        </RefreshRedirect>
    );
}
